import React, { useEffect, useState } from "react";
import { Doughnut, Line } from "react-chartjs-2";
import { ReactComponent as CircleUp } from "../../assets/img/circle-up.svg";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const Statistics = ({
  statsData,
  setStatsData,
  getAllClients,
  chartData,
  setChartData,
  clientData,
}) => {
  const [showJobs, setShowJobs] = useState(true);
  const [showEmployers, setShowEmployers] = useState(true);
  const [showWorkers, setShowWorkers] = useState(true);
  const [isApplying, setIsApplying] = useState(false);

  // Estado temporal para fechas antes de aplicarlas
  const [pendingDates, setPendingDates] = useState({
    date_start: chartData.date_start,
    date_end: chartData.date_end,
  });

  const getDaysArray = (start, end) => {
    const arr = [];
    let dt = new Date(start);
    const endDate = new Date(end);
    while (dt <= endDate) {
      arr.push(dt.toISOString().slice(0, 10));
      dt.setDate(dt.getDate() + 1);
    }
    return arr;
  };

  useEffect(() => {
    ["all", "validated", "active"].forEach((type) => getAllClients(type));
  }, []);

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "right",
        labels: { boxWidth: 12, boxHeight: 12, usePointStyle: true },
      },
    },
  };

  const getDoughnutData = (arr, labelKey) => ({
    labels: arr?.map((c) => c[labelKey]) || [],
    datasets: [
      {
        data: arr?.map((c) => parseInt(c.cant || 0)) || [],
        backgroundColor: [
          "#2563EB",
          "#ed4685",
          "#FBBF24",
          "#34D399",
          "#60A5FA",
        ],
        borderWidth: 2,
      },
    ],
  });

  const workersCount = clientData?.workers?.length || 0;
  const employersCount = clientData?.employers?.length || 0;
  const jobsCount = statsData?.totalJobs || 0;

  const balanceItems = [
    {
      label: "new workers",
      value: workersCount,
      icon: CircleUp,
      color: "green-500",
      diff: "+0",
    },
    {
      label: "new employers",
      value: employersCount,
      icon: CircleUp,
      color: "green-500",
      diff: "+0",
    },
    {
      label: "new job posts",
      value: jobsCount,
      icon: CircleUp,
      color: "green-500",
      diff: "+0",
    },
  ];

  // ---------------------- Line Chart ----------------------
  const lineLabels =
    chartData.date_start && chartData.date_end
      ? getDaysArray(chartData.date_start, chartData.date_end)
      : [];

  const getDailyData = (dataArr, date) => {
    const item = dataArr?.find((d) => {
      const dataDate = new Date(d.date_created).toISOString().slice(0, 10);
      return dataDate === date;
    });
    return item ? parseInt(item.day_value) : 0;
  };

  const jobsDaily = lineLabels.map((date) =>
    getDailyData(chartData?.fetchedData?.jobs || [], date)
  );
  const employersDaily = lineLabels.map((date) =>
    getDailyData(chartData?.fetchedData?.employers || [], date)
  );
  const workersDaily = lineLabels.map((date) =>
    getDailyData(chartData?.fetchedData?.workers || [], date)
  );

  const dataLine = {
    labels: lineLabels.map((date) => {
      const d = new Date(date);
      return `${d.getDate()}/${d.getMonth() + 1}`;
    }),
    datasets: [
      {
        label: "Jobs",
        data: jobsDaily,
        borderColor: "#FBBF24",
        backgroundColor: "#FBBF2433",
        tension: 0.3,
        fill: true,
        pointRadius: 5,
        pointHoverRadius: 7,
        hidden: !showJobs,
      },
      {
        label: "Employers",
        data: employersDaily,
        borderColor: "#2563EB",
        backgroundColor: "#2563EB33",
        tension: 0.3,
        fill: true,
        pointRadius: 5,
        pointHoverRadius: 7,
        hidden: !showEmployers,
      },
      {
        label: "Workers",
        data: workersDaily,
        borderColor: "#ed4685",
        backgroundColor: "#ed468533",
        tension: 0.3,
        fill: true,
        pointRadius: 5,
        pointHoverRadius: 7,
        hidden: !showWorkers,
      },
    ],
  };

  const lineOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.raw}` },
      },
      title: { display: true, text: "Daily Activity", font: { size: 16 } },
    },
    interaction: { mode: "nearest", axis: "x", intersect: false },
    scales: {
      x: { title: { display: true, text: "Date (DD/MM)" } },
      y: {
        title: { display: true, text: "Count" },
        beginAtZero: true,
        ticks: { stepSize: 1 },
      },
    },
  };

  const handleApplyDates = async () => {
    const { date_start, date_end } = pendingDates;
    if (!date_start || !date_end) {
      alert("Por favor, introduce ambas fechas antes de aplicar.");
      return;
    }
    if (new Date(date_start) > new Date(date_end)) {
      alert("La fecha de inicio no puede ser posterior a la fecha de fin.");
      return;
    }

    setIsApplying(true);
    await new Promise((res) => setTimeout(res, 500));

    setChartData((prev) => ({
      ...prev,
      date_start,
      date_end,
    }));

    setIsApplying(false);
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg md:p-6 flex flex-col gap-6 overflow-auto">
      {/* ---------------------- Balance Section ---------------------- */}
      <div className="bg-[#F9F9F9] rounded-xl p-2 md:p-6 flex flex-col gap-6">
        <p className="font-bold text-lg text-gray-700">Balance</p>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left: Line Chart */}
          <div className="flex-1 min-w-0 flex flex-col gap-4">
            <div className="flex flex-wrap gap-2 mb-2">
              <button
                onClick={() => setShowJobs((prev) => !prev)}
                className={`px-4 py-2 rounded-lg font-semibold ${
                  showJobs
                    ? "bg-[#FBBF24] text-white"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                Jobs
              </button>
              <button
                onClick={() => setShowEmployers((prev) => !prev)}
                className={`px-4 py-2 rounded-lg font-semibold ${
                  showEmployers
                    ? "bg-[#2563EB] text-white"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                Employers
              </button>
              <button
                onClick={() => setShowWorkers((prev) => !prev)}
                className={`px-4 py-2 rounded-lg font-semibold ${
                  showWorkers
                    ? "bg-[#ed4685] text-white"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                Workers
              </button>
            </div>

            <div className="w-full h-80 xl:h-[400px]">
              <Line
                options={{ ...lineOptions, maintainAspectRatio: false }}
                data={dataLine}
              />
            </div>
          </div>

          {/* Right: Date selectors + Balance Cards */}
          <div className="flex flex-col gap-4 w-full lg:w-1/3 min-w-0">
            {/* Fechas y botón */}
            <div className="flex flex-col gap-2 mb-4">
              <div className="flex flex-col sm:flex-row gap-2 w-full items-end">
                <div className="flex flex-col w-full sm:w-32">
                  <label className="text-sm text-gray-600">Start Date</label>
                  <input
                    type="date"
                    value={pendingDates.date_start || ""}
                    onChange={(e) =>
                      setPendingDates((prev) => ({
                        ...prev,
                        date_start: e.target.value,
                      }))
                    }
                    className="border rounded px-2 py-1 text-center w-full"
                  />
                </div>
                <div className="flex flex-col w-full sm:w-32">
                  <label className="text-sm text-gray-600">End Date</label>
                  <input
                    type="date"
                    value={pendingDates.date_end || ""}
                    onChange={(e) =>
                      setPendingDates((prev) => ({
                        ...prev,
                        date_end: e.target.value,
                      }))
                    }
                    className="border rounded px-2 py-1 text-center w-full"
                  />
                </div>
                <div className="w-full sm:w-auto flex justify-end">
                  <button
                    onClick={handleApplyDates}
                    disabled={isApplying}
                    className="flex items-center justify-center gap-2 bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition disabled:opacity-60 w-full sm:w-auto"
                  >
                    {isApplying ? (
                      <>
                        <span className="animate-spin border-2 border-t-transparent border-white rounded-full w-4 h-4" />
                        Applying...
                      </>
                    ) : (
                      "Apply Range"
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Balance Cards */}
            <div className="flex flex-col gap-4">
              {balanceItems.map(({ label, value, diff, icon: Icon, color }) => (
                <div
                  key={label}
                  className="bg-white shadow-md rounded-2xl p-4 flex flex-col items-center justify-center w-full h-[120px]"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={`h-6 w-6 fill-${color}`} />
                    <p className="text-sm text-gray-500 uppercase">{label}</p>
                  </div>
                  <p className="text-3xl font-bold text-[#2563EB]">{value}</p>
                  <p className="text-xs text-gray-400">
                    {diff} than last month
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Doughnut Charts Section */}
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              title: "All Clients",
              data: statsData.clientsType || [],
              labelKey: "client_type",
            },
            {
              title: "Validated Status",
              data: statsData.validatedStatus || [],
              labelKey: "validated_status",
            },
            {
              title: "Active Status",
              data: statsData.activeStatus || [],
              labelKey: "active_status",
            },
          ].map(({ title, data, labelKey }) => (
            <div
              key={title}
              className="bg-[#F9F9F9] rounded-xl shadow-sm p-4 flex flex-col items-center justify-center gap-4 h-[280px]"
            >
              <div className="text-gray-700 font-bold text-lg">{title}</div>
              <div className="flex-1 flex items-center justify-center w-full lg:w-1/2">
                <Doughnut
                  data={getDoughnutData(data, labelKey)}
                  options={{ ...doughnutOptions, cutout: "70%" }}
                />
                <div className="flex flex-col items-center justify-center w-full lg:w-1/2">
                  <p className="text-3xl font-bold">
                    {data.reduce((sum, c) => sum + parseInt(c.cant || 0), 0)}
                  </p>
                  <p className="text-sm text-gray-500">TOTAL</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Statistics;
